import { trackEvent } from '@/lib/trackEvent';

export const SCROLL_DEPTH_MILESTONES = [25, 50, 75, 100] as const;

export type ScrollDepthMilestone = (typeof SCROLL_DEPTH_MILESTONES)[number];

const OPENED_AT_KEY_PREFIX = 'ph_report_opened_at:';
const SCROLLED_MILESTONES_KEY_PREFIX = 'ph_report_scrolled:';

const getOpenedAtKey = (reportId: string) => `${OPENED_AT_KEY_PREFIX}${reportId}`;

const getScrolledKey = (reportId: string) =>
  `${SCROLLED_MILESTONES_KEY_PREFIX}${reportId}`;

export const markReportOpenedAt = (reportId: string, at: number = Date.now()): void => {
  if (typeof sessionStorage === 'undefined' || !reportId) return;
  if (sessionStorage.getItem(getOpenedAtKey(reportId))) return;
  sessionStorage.setItem(getOpenedAtKey(reportId), String(at));
};

export const getReportOpenedAt = (reportId: string): number => {
  if (typeof sessionStorage === 'undefined' || !reportId) return Date.now();
  const stored = sessionStorage.getItem(getOpenedAtKey(reportId));
  if (stored) {
    const parsed = Number(stored);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const now = Date.now();
  markReportOpenedAt(reportId, now);
  return now;
};

const readFiredMilestones = (reportId: string): Set<ScrollDepthMilestone> => {
  if (typeof sessionStorage === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(getScrolledKey(reportId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as number[];
    return new Set(
      parsed.filter((value): value is ScrollDepthMilestone =>
        SCROLL_DEPTH_MILESTONES.includes(value as ScrollDepthMilestone)
      )
    );
  } catch {
    return new Set();
  }
};

const writeFiredMilestones = (
  reportId: string,
  milestones: Set<ScrollDepthMilestone>
): void => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(
    getScrolledKey(reportId),
    JSON.stringify(Array.from(milestones))
  );
};

export const getScrollDepthPercent = (): number => {
  const doc = document.documentElement;
  const scrollHeight = Math.max(
    doc.scrollHeight,
    document.body?.scrollHeight ?? 0
  );
  if (scrollHeight <= 0) return 0;

  const scrolled = window.scrollY + window.innerHeight;
  return Math.min(100, Math.round((scrolled / scrollHeight) * 100));
};

export const trackScrollMilestones = (
  reportId: string,
  depthPercent: number = getScrollDepthPercent()
): ScrollDepthMilestone[] => {
  if (!reportId) return [];

  const fired = readFiredMilestones(reportId);
  const openedAt = getReportOpenedAt(reportId);
  const timeToDepthSeconds = Math.max(
    0,
    Math.floor((Date.now() - openedAt) / 1000)
  );
  const newlyFired: ScrollDepthMilestone[] = [];

  for (const milestone of SCROLL_DEPTH_MILESTONES) {
    if (fired.has(milestone)) continue;
    if (depthPercent < milestone) continue;

    const tracked = trackEvent('report_scrolled', {
      scroll_depth: milestone,
      time_to_depth_seconds: timeToDepthSeconds,
    });

    if (!tracked) continue;

    fired.add(milestone);
    newlyFired.push(milestone);
  }

  if (newlyFired.length > 0) {
    writeFiredMilestones(reportId, fired);
  }

  return newlyFired;
};

export const startReportScrolledTracking = (reportId: string): (() => void) => {
  if (!reportId || typeof window === 'undefined') {
    return () => undefined;
  }

  getReportOpenedAt(reportId);

  let frameId = 0;

  const evaluate = () => {
    frameId = 0;
    trackScrollMilestones(reportId);
  };

  const handleScroll = () => {
    if (frameId) return;
    frameId = window.requestAnimationFrame(evaluate);
  };

  evaluate();
  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => {
    window.removeEventListener('scroll', handleScroll);
    if (frameId) {
      window.cancelAnimationFrame(frameId);
    }
  };
};
